import {
  Building2,
  HeartPulse,
  GraduationCap,
  ShoppingBag,
  Factory,
  Laptop2,
  Landmark,
  Hotel,
  Truck,
  Home,
  Briefcase,
  Globe2,
  ShieldCheck,
  Sparkles,
  ArrowUpRight,
  Users,
} from "lucide-react";

const industries = [
  {
    slug: "real-estate",
    icon: Building2,
    name: "Real Estate",
    badge: "High growth",
    headline: "Premium lead generation for developers, builders and brokers.",
    description:
      "We combine branding, performance marketing, CRM automation and elegant web experiences to generate qualified buyers while improving project visibility.",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1800&q=80",
    stats: [
      { label: "Lead quality", value: "+68%" },
      { label: "CPL reduction", value: "-42%" },
      { label: "Sales velocity", value: "+39%" },
    ],
    challenges: [
      "High acquisition cost",
      "Low quality leads",
      "Slow follow-up",
      "Project visibility",
    ],
    services: [
      "Brand strategy",
      "Luxury website",
      "Performance marketing",
      "CRM automation",
      "Lead qualification",
      "Analytics dashboard",
    ],
    outcomes: [
      "Premium positioning",
      "Consistent buyer enquiries",
      "Automated nurturing",
      "Higher conversion rate",
    ],
    focusPoints: ["Luxury positioning", "Buyer journeys", "Automated follow-up"],
  },
  {
    slug: "healthcare",
    icon: HeartPulse,
    name: "Healthcare",
    badge: "Trust driven",
    headline: "Build digital trust that converts patients into long-term relationships.",
    description:
      "From multi-speciality hospitals to clinics, we create secure digital ecosystems focused on credibility, patient acquisition and retention.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1800&q=80",
    stats: [
      { label: "Appointments", value: "+74%" },
      { label: "Organic reach", value: "+210%" },
      { label: "Retention", value: "+51%" },
    ],
    challenges: [
      "Low online visibility",
      "Trust building",
      "Patient retention",
      "Manual booking",
    ],
    services: [
      "Healthcare branding",
      "SEO",
      "Appointment system",
      "Marketing automation",
      "Content strategy",
      "Analytics",
    ],
    outcomes: ["More appointments", "Higher credibility", "Better engagement", "Improved retention"],
    focusPoints: ["Patient trust", "Conversion funnels", "Retention systems"],
  },
  {
    slug: "education",
    icon: GraduationCap,
    name: "Education",
    badge: "Admissions",
    headline: "Increase admissions through modern digital experiences.",
    description:
      "Universities, schools and EdTech platforms leverage intelligent campaigns and immersive websites to improve student acquisition.",
    image:
      "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1800&q=80",
    stats: [
      { label: "Admissions", value: "+57%" },
      { label: "Applications", value: "+91%" },
      { label: "Engagement", value: "+123%" },
    ],
    challenges: [
      "Student acquisition",
      "Competition",
      "Digital identity",
      "Parent trust",
    ],
    services: [
      "Institution branding",
      "Admission funnels",
      "SEO",
      "Performance marketing",
      "Video marketing",
      "CRM",
    ],
    outcomes: ["Higher enquiries", "Better conversion", "Brand authority", "Admission growth"],
    focusPoints: ["Admissions funnels", "Parent confidence", "Video-led outreach"],
  },
  {
    slug: "ecommerce",
    icon: ShoppingBag,
    name: "E-Commerce",
    badge: "Scale",
    headline: "Accelerate online sales with data-driven commerce experiences.",
    description:
      "We help modern brands improve acquisition, retention and customer lifetime value using AI-driven growth systems.",
    image:
      "https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=1800&q=80",
    stats: [
      { label: "ROAS", value: "5.8X" },
      { label: "Revenue", value: "+180%" },
      { label: "LTV", value: "+47%" },
    ],
    challenges: ["Low ROAS", "Abandoned carts", "Retention", "Scaling"],
    services: [
      "Conversion optimization",
      "Paid ads",
      "Email automation",
      "Landing pages",
      "Growth analytics",
      "Creative strategy",
    ],
    outcomes: ["Revenue growth", "Higher AOV", "Repeat customers", "Improved ROAS"],
    focusPoints: ["Merchandise storytelling", "Checkout optimization", "Lifecycle marketing"],
  },
  {
    slug: "manufacturing",
    icon: Factory,
    name: "Manufacturing",
    badge: "B2B",
    headline: "Modernize industrial businesses with enterprise digital transformation.",
    description:
      "We create digital ecosystems that help manufacturers generate qualified B2B opportunities and strengthen market presence.",
    image:
      "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=1800&q=80",
    stats: [
      { label: "Qualified leads", value: "+84%" },
      { label: "Global reach", value: "+170%" },
      { label: "Conversion", value: "+43%" },
    ],
    challenges: [
      "Outdated websites",
      "International visibility",
      "Lead generation",
      "Brand positioning",
    ],
    services: [
      "Corporate website",
      "SEO",
      "Lead funnels",
      "Automation",
      "Industrial branding",
      "Analytics",
    ],
    outcomes: ["Better enquiries", "Global visibility", "Sales growth", "Digital authority"],
    focusPoints: ["B2B authority", "Lead qualification", "Global visibility"],
  },
  {
    slug: "saas",
    icon: Laptop2,
    name: "SaaS & Technology",
    badge: "AI ready",
    headline: "Acquire, activate and retain users with scalable growth systems.",
    description:
      "Helping SaaS startups and enterprise software companies create product-led growth through premium UX, AI and analytics.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1800&q=80",
    stats: [
      { label: "MRR", value: "+63%" },
      { label: "Activation", value: "+52%" },
      { label: "Retention", value: "+34%" },
    ],
    challenges: ["User acquisition", "Activation", "Retention", "Product adoption"],
    services: [
      "Growth strategy",
      "Product marketing",
      "Landing pages",
      "UX optimization",
      "Analytics",
      "AI automation",
    ],
    outcomes: ["Lower CAC", "Higher retention", "MRR growth", "Faster expansion"],
    focusPoints: ["Product-led growth", "Activation flows", "Retention analytics"],
  },
];

export const industryFeatures = [
  { icon: Globe2, title: "Market intelligence" },
  { icon: Users, title: "Customer psychology" },
  { icon: Sparkles, title: "Premium experience" },
  { icon: ArrowUpRight, title: "Revenue optimization" },
  { icon: ShieldCheck, title: "Enterprise security" },
  { icon: Landmark, title: "Scalable systems" },
  { icon: Hotel, title: "Hospitality growth" },
  { icon: Truck, title: "Supply chain" },
  { icon: Home, title: "Infrastructure" },
  { icon: Briefcase, title: "Business consulting" },
];

export const industryApproach = [
  {
    step: "01",
    title: "Discover",
    text: "We map the market, audience and current digital performance before any design decisions are made.",
  },
  {
    step: "02",
    title: "Position",
    text: "We sharpen the story so your brand feels more credible, more premium and easier to choose.",
  },
  {
    step: "03",
    title: "Launch",
    text: "We build the experience, campaigns and automation that turn attention into action.",
  },
  {
    step: "04",
    title: "Scale",
    text: "We refine based on data so the system improves with every cycle instead of stagnating.",
  },
];

export default industries;
